import * as React from 'react';
import { RadioGroup as ChakraRadioGroup, Field, Box, Stack } from '@chakra-ui/react';

type ValueChangeDetails = {
  value: string | null;
};

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  label?: string;
  error?: string;
  hint?: string;
  options: RadioOption[];
  containerClassName?: string;
  radioSize?: 'sm' | 'md' | 'lg';
  required?: boolean;
  disabled?: boolean;
  direction?: 'row' | 'column';
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      label,
      error,
      hint,
      options = [],
      containerClassName,
      radioSize = 'md',
      required,
      disabled,
      direction = 'column',
      defaultValue,
      value,
      onChange,
      ...props
    },
    _ref
  ) => {
    // Determine field status
    const invalid = !!error;

    // Map sizes
    const sizeMap = {
      sm: 'sm',
      md: 'md',
      lg: 'lg',
    };

    // Filter out invalid props that shouldn't be passed to DOM
    const { invalid: _invalid, ...filteredProps } = props as Record<string, unknown>;

    // Radio control styles to fix visibility issue
    const radioControlStyle = {
      borderColor: invalid ? '#EF4444' : '#CED4DA', // Use proper gray border instead of white
      _hover: {
        borderColor: invalid ? '#DC2626' : '#9CA3AF',
      },
      _checked: {
        borderColor: '#0066CC',
        backgroundColor: '#0066CC',
      },
      _disabled: {
        borderColor: '#E9ECEF',
        backgroundColor: '#F8F9FA',
        opacity: 0.6,
      },
      // Use proper Chakra UI v3 compound selector syntax
      _hover_checked: {
        borderColor: '#0052A3',
        backgroundColor: '#0052A3',
      },
    };

    return (
      <Field.Root
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label */}
        {label && (
          <Box
            as="label"
            fontSize="sm"
            fontWeight="medium"
            color={invalid ? '#EF4444' : '#374151'}
            display="block"
            marginBottom="4px"
          >
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Radio Group */}
        <ChakraRadioGroup.Root
          size={sizeMap[radioSize] as 'sm' | 'md' | 'lg'}
          disabled={disabled}
          defaultValue={defaultValue}
          {...(value !== undefined &&
          value !== null &&
          value !== '' &&
          options.some((opt) => opt.value === value)
            ? { value }
            : {})}
          onValueChange={(details: ValueChangeDetails) => {
            if (details.value) {
              onChange?.(details.value);
            }
          }}
          {...filteredProps}
        >
          <Stack direction={direction} gap={3}>
            {options.map((option) => {
              return React.createElement(
                ChakraRadioGroup.Item as unknown as React.ComponentType<Record<string, unknown>>,
                {
                  key: option.value,
                  value: option.value,
                  disabled: option.disabled || disabled,
                },
                React.createElement(ChakraRadioGroup.ItemHiddenInput, { key: 'hidden' }),
                React.createElement(ChakraRadioGroup.ItemControl, {
                  key: 'control',
                  ...radioControlStyle,
                }),
                React.createElement(ChakraRadioGroup.ItemText, { key: 'text' }, option.label)
              );
            })}
          </Stack>
        </ChakraRadioGroup.Root>

        {/* Error Message */}
        {error && (
          <Box fontSize="xs" color="#EF4444" marginTop="4px">
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="xs" color="#4B5563" marginTop="4px">
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

// Export composable components for advanced usage
export const RadioGroupRoot = ChakraRadioGroup.Root;
export const RadioGroupItem = ChakraRadioGroup.Item;
export const RadioGroupItemHiddenInput: React.ComponentType = ChakraRadioGroup.ItemHiddenInput;
export const RadioGroupItemControl = ChakraRadioGroup.ItemControl;
export const RadioGroupItemIndicator: React.ComponentType = ChakraRadioGroup.ItemIndicator;
export const RadioGroupItemText = ChakraRadioGroup.ItemText;

export { RadioGroup };

// Export Radio as alias for RadioGroupItem for compatibility
export const Radio = ChakraRadioGroup.Item;
